import { useState, useEffect, useRef } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";

//Components
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import FilterSteps from "./FilterSteps";

//Styling
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

import { faTrash } from "@fortawesome/free-solid-svg-icons";


import "../../../css/AdminSteps.css";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AdminStepsBulk({ isNavOpen, allowedRoles, customRoles }) {

  const { auth } = useAuth();
  const isSuperAdmin = auth.roles.includes("SuperAdmin");
  const isAdmin = auth.roles.includes("Admin");
  const userId = auth?.userId;

  const navigate = useNavigate();

  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();

  const [activeSteps, setActiveSteps] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedSteps, setSelectedSteps] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showConfirmBulkDelete, setShowConfirmBulkDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';
const DEMO_PROTECTED_STEP_IDS = [1, 2, 3];

  const prevError = useRef(null);

  // 🧠 Reset error whenever filters change
  useEffect(() => {
    setError(null);
  }, []);

  // ✅ Fetch active steps (with backend filtering)
  useEffect(() => {
    const fetchActiveSteps = async () => {
      if (!userId) return;
      setIsLoading(true);
      setError(null);

      try {
        const response = await axiosPrivate.post("/steps/active", {
          userId,
          filters, // send filters to backend
        });
        setActiveSteps(response.data);
      } catch (err) {
        console.error("Error fetching active steps:", err);
        setError(
          err.response?.data?.message ||
          t("adminSteps.errorFetchSteps", "Failed to load active steps.")
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchActiveSteps();
  }, [axiosPrivate, userId, filters, t]);


  if (!auth?.roles) return null;
  if (!isSuperAdmin && !isAdmin) return null;

  // ✅ Toggle selected step
  const handleSelectStep = async (stepId, currentValue) => {
    setError(null);
    try {
      // Optimistic UI update
      setActiveSteps((prev) =>
        prev.map((step) =>
          step.step_id === stepId
            ? { ...step, is_selected: !currentValue }
            : step
        )
      );

      // Send update to backend
      await axiosPrivate.patch(`/steps/${stepId}/select`, {
        is_selected: !currentValue,
        userId,
      });
    } catch (err) {
      console.error("Error updating step selection:", err);
      setError(
        err.response?.data?.message ||
        t("adminStepsBulk.errorUpdatingSelect", "Failed to update selection.")
      );
    }
  };


  // ✅ Placeholder edit/delete logic
  const handleEditStep = (step, role) => {
    if (!step?.step_id || !role?.role_slug) return;
    navigate(`/roles/${role.role_slug}/steps/${step.step_id}`);
  };

  const handleDeleteStep = async (stepId) => {
    setError(null);
    if (!stepId) return;
    setIsDeleting(true);

    try {
      await axiosPrivate.delete(`/steps/${stepId}`, { data: { userId } });
      setActiveSteps((prev) => prev.filter((step) => step.step_id !== stepId));
      setShowConfirmDelete(null);
    } catch (err) {
      console.error("Error deleting step:", err);

      const status = err?.response?.status;

      // Prefer backend error text
      const backendMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t("adminStepsBulk.errorDeletingStep", "Failed to delete step.");

      // 👉 If 403, show THAT message
      if (status === 403) {
        setError(backendMessage);
        return;
      }

      // 👉 All other errors
      setError(
        t("adminStepsBulk.errorDeletingStep", "Failed to delete step.")
      );
    }
    finally {
      setIsDeleting(false);
    }
  };

  // ✅ Bulk delete selected steps
const handleBulkDelete = async () => {
  setError(null);

  let selectedSteps = activeSteps.filter(s => s.is_selected);

  // 🚫 DEMO MODE GUARD
  if (IS_DEMO_MODE) {
    const hasProtected = selectedSteps.some(step =>
      DEMO_PROTECTED_STEP_IDS.includes(step.step_id)
    );

    if (hasProtected) {
      setError(
        t(
          "adminStepsBulk.demoProtectedSteps",
          "Demo mode: steps 1, 2, and 3 cannot be deleted."
        )
      );
      return;
    }
  }

  const selectedStepIds = selectedSteps.map(s => s.step_id);
  if (selectedStepIds.length === 0) return;

  setIsBulkDeleting(true);

  try {
    await axiosPrivate.delete("/steps/bulk-delete", {
      data: { userId, stepIds: selectedStepIds }
    });

    setActiveSteps(prev =>
      prev.filter(step => !selectedStepIds.includes(step.step_id))
    );

    setShowConfirmBulkDelete(false);
  } catch (err) {
    setError(
      err.response?.data?.message ||
      t("adminStepsBulk.errorDeletingSelected")
    );
  } finally {
    setIsBulkDeleting(false);
  }
};


  // ✅ Group steps by roles
  const getStepsForRole = (roleId) => {
    return activeSteps.filter((step) => step.role_ids?.includes(roleId));
  };

  return (
    <div className={`${isNavOpen ? "body-squeezed" : "body"}`}>
      <div className="admin-steps tx1">
        <h2
          style={{ marginBottom: '1em', alignSelf: "center" }}
        >{t("adminStepsBulk.title")}</h2>


        <FilterSteps customRoles={customRoles} setFilters={setFilters} />


        {isLoading && <LoadingSpinner />}
        {error && <div className="error-message">{error}</div>}


        {/* Bulk delete selected steps */}
        <div className="admin-steps-actions">
          {showConfirmBulkDelete ? (
            <div className="confirm-bulk-delete">
              <button
                className="button-red button-smaller"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting
                  ? t("adminStepsBulk.deleting")
                  : t("adminStepsBulk.confirmDelete")}
              </button>
              <button
                className="button-white button-smaller"
                onClick={() => {
                  setShowConfirmBulkDelete(false);
                  setError(null);
                }}
                disabled={isBulkDeleting}
              >
                {t("adminStepsBulk.cancel")}
              </button>
            </div>
          ) : (
            <button
              className="button-red button-smaller"
              style={{ marginBottom: "1em", marginTop: "1em" }}
              disabled={!activeSteps.some((s) => s.is_selected)}
              onClick={() => setShowConfirmBulkDelete(true)}
            >
              {t("adminStepsBulk.deleteAllSelected")}
            </button>
          )}
        </div>



        {/* ✅ Only show roles that have steps */}
        {(() => {
          // Find which roles have at least one step
          const rolesWithSteps = customRoles.filter(
            (role) => getStepsForRole(role.role_id).length > 0
          );

          // If none, show fallback message
          if (rolesWithSteps.length === 0) {
            return (
              <p style={{ color: "gray", marginTop: "2rem" }}>
                {t("adminStepsBulk.noStepsForRole")}
              </p>
            );
          }

          // Otherwise, render only roles that have steps
          return rolesWithSteps.map((role) => {
            const roleSteps = getStepsForRole(role.role_id);

            return (
              <div key={role.role_id} style={{ marginBottom: "2rem" }}>
                <h3 style={{ marginBottom: "0.5rem" }}>
                  {role.role_name || t("adminStepsBulk.untitledRole")}
                </h3>

                <div
                  className="admin-steps-table-container"
                  style={{ overflowX: "auto" }}
                >
                  <table className="admin-steps-table">
                    <thead>
                      <tr>
                        <th>{t("adminStepsBulk.columnSelect")}</th>
                        <th></th>
                        <th>{t("adminStepsBulk.columnName")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleSteps.map((step) => (
                        <tr key={step.step_id}>
                          {/* Checkbox */}
                          <td>
                            <input
                              type="checkbox"
                              checked={step.is_selected}
                              onChange={() =>
                                handleSelectStep(step.step_id, step.is_selected)
                              }
                              aria-label={t("adminStepsBulk.checkboxLabel")}
                            />
                          </td>

                          {/* Delete Button (moved next to checkbox) */}
                          <td>
                            {showConfirmDelete === step.step_id ? (
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button
                                  className="button-red button-smaller"
                                  onClick={() => handleDeleteStep(step.step_id)}
                                  disabled={isDeleting}
                                >
                                  {isDeleting
                                    ? t("adminStepsBulk.deleting")
                                    : t("adminStepsBulk.confirmDelete")}
                                </button>
                                <button
                                  className="button-white button-smaller"
                                  onClick={() => {
                                    setShowConfirmDelete(null);
                                    setError(null);
                                  }}
                                  disabled={isDeleting}
                                >
                                  {t("adminStepsBulk.cancel")}
                                </button>
                              </div>
                            ) : (
                              <button
                                className="button-red button-smaller"
                                onClick={() => setShowConfirmDelete(step.step_id)}
                                disabled={isDeleting}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </td>

                          {/* Step Name (clickable link) */}
                          <td
                            className="step-name-cell"
                            onClick={() => navigate(`/roles/${role.role_slug}/steps/${step.step_id}`)}
                            style={{ cursor: "pointer" }}
                          >
                            {step.step_name}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>
              </div>
            );
          });
        })()}




      </div>
    </div>
  );
}
